import React, { useState } from "react";
import {
  ArrowRight,
  ChevronDown,
  CreditCard,
  HelpCircle,
  Package,
  RotateCcw,
  Search,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { Link } from "react-router-dom";

const FAQ_DATA = [
  {
    category: "orders",
    categoryTitle: "Orders & Delivery",
    icon: Package,
    items: [
      {
        q: "How do I track my order status?",
        a: "As soon as your order is dispatched, we send you an SMS and email with an Air Waybill (AWB) tracking number and live link. You can also log in and check the Orders section anytime.",
      },
      {
        q: "How long does delivery usually take?",
        a: "Orders are dispatched within 24 to 48 business hours. Delivery to major metro and tier-1 cities takes 2 to 4 business days. Rest of India takes 4 to 7 business days depending on location.",
      },
      {
        q: "Is there any delivery charge?",
        a: "We offer Free Express Delivery on all orders totaling ₹499 or more! For orders below ₹499, a nominal standard shipping fee is applied at checkout.",
      },
      {
        q: "Can I change my delivery address after placing an order?",
        a: "If your order has not yet been dispatched, please contact our customer support team immediately via WhatsApp or phone, and we will update your delivery address for you.",
      },
    ],
  },
  {
    category: "payments",
    categoryTitle: "Payments & COD",
    icon: CreditCard,
    items: [
      {
        q: "What payment options are available?",
        a: "We support all major payment modes including UPI (Google Pay, PhonePe, Paytm, BHIM), Credit & Debit cards (Visa, MasterCard, RuPay), Netbanking across all Indian banks, and Cash on Delivery (COD).",
      },
      {
        q: "Is it safe to pay online on Sugam Store?",
        a: "Yes, 100%! All digital payments are processed through RBI-authorized, 256-bit SSL encrypted payment gateways like Razorpay. We never store your full card details or CVV on our servers.",
      },
      {
        q: "How does Cash on Delivery (COD) work?",
        a: "Select 'Cash on Delivery' on the checkout page. You pay the exact order amount in cash or digital UPI to the courier delivery executive when your parcel arrives at your door.",
      },
      {
        q: "My money was deducted but order was not confirmed. What should I do?",
        a: "In rare banking gateway delays, deducted money is automatically reversed to your bank account within 24 to 48 hours. If you need urgent assistance, send us your transaction reference ID via Contact Us.",
      },
    ],
  },
  {
    category: "returns",
    categoryTitle: "Returns & Refunds",
    icon: RotateCcw,
    items: [
      {
        q: "What is your return policy?",
        a: "We offer a 7-day hassle-free return and replacement policy for eligible products. If an item arrives damaged, defective, or different from described, you can request a replacement or full refund.",
      },
      {
        q: "How do I request a return or exchange?",
        a: "Go to your Orders section, choose the order, and click 'Return Item', or reach out to our team at support@sugamstore.in with your order number. We will arrange a free doorstep reverse pickup.",
      },
      {
        q: "When will I receive my refund?",
        a: "Once the returned parcel is received and inspected at our hub, refunds for prepaid orders are credited back to your original source within 3-5 business days. For COD orders, we transfer via UPI or bank NEFT within 2-4 days.",
      },
    ],
  },
  {
    category: "account",
    categoryTitle: "Account & Security",
    icon: ShieldCheck,
    items: [
      {
        q: "Do I need an account to shop on Sugam Store?",
        a: "You can browse and add items to your bag anytime! When completing checkout or saving your favorite items to wishlist, we use a quick, passwordless 6-digit OTP sent to your phone number for effortless security.",
      },
      {
        q: "How can I update my saved delivery addresses?",
        a: "Log in with your mobile OTP, head over to your Profile page, and select 'Addresses'. You can add new addresses, edit existing ones, or choose your default shipping address.",
      },
    ],
  },
];

export default function Faq() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (categoryIndex, itemIndex) => {
    const key = `${categoryIndex}-${itemIndex}`;
    setOpenItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const filteredCategories = FAQ_DATA.map((cat) => {
    const matchingItems = cat.items.filter((item) => {
      const matchesSearch =
        item.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.a.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });

    return { ...cat, items: matchingItems };
  }).filter((cat) => {
    if (activeCategory !== "all" && cat.category !== activeCategory) return false;
    return cat.items.length > 0;
  });

  return (
    <main className="page-shell content-page faq-page">
      <div className="breadcrumbs">
        <Link to="/">Home</Link>
        <span>/</span>
        <span>Help & FAQs</span>
      </div>

      <section className="faq-hero">
        <span className="eyebrow">
          <Sparkles size={14} /> HOW CAN WE HELP YOU?
        </span>
        <h1>Frequently Asked Questions</h1>
        <p>Quick answers to common questions about orders, delivery, payments, and returns.</p>

        <div className="faq-search-box">
          <Search size={18} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search keywords (e.g. delivery, refund, tracking, COD)..."
            aria-label="Search FAQs"
          />
          {searchTerm && (
            <button className="faq-search-clear" onClick={() => setSearchTerm("")}>
              Clear
            </button>
          )}
        </div>
      </section>

      {/* Category Pills */}
      <div className="faq-category-pills">
        <button
          className={activeCategory === "all" ? "is-selected" : ""}
          onClick={() => setActiveCategory("all")}
        >
          All Topics
        </button>
        {FAQ_DATA.map((cat) => (
          <button
            key={cat.category}
            className={activeCategory === cat.category ? "is-selected" : ""}
            onClick={() => setActiveCategory(cat.category)}
          >
            {cat.categoryTitle}
          </button>
        ))}
      </div>

      {/* Accordion Lists */}
      <div className="faq-content-container">
        {filteredCategories.length === 0 ? (
          <div className="empty-state">
            <span className="empty-state__icon">
              <HelpCircle size={28} />
            </span>
            <h2>No results found for “{searchTerm}”</h2>
            <p>Try searching with another keyword or connect with our customer support directly.</p>
            <Link to="/contact" className="button button--primary">
              Contact Customer Support <ArrowRight size={17} />
            </Link>
          </div>
        ) : (
          filteredCategories.map((cat, catIdx) => (
            <section key={cat.category} className="faq-section-group">
              <div className="faq-section-heading">
                <cat.icon size={22} />
                <h2>{cat.categoryTitle}</h2>
              </div>
              <div className="faq-accordion-list">
                {cat.items.map((item, itemIdx) => {
                  const key = `${catIdx}-${itemIdx}`;
                  const isOpen = Boolean(openItems[key]);
                  return (
                    <div className={`faq-item${isOpen ? " is-open" : ""}`} key={item.q}>
                      <button
                        className="faq-question"
                        onClick={() => toggleItem(catIdx, itemIdx)}
                        aria-expanded={isOpen}
                      >
                        <span>{item.q}</span>
                        <ChevronDown size={18} className="faq-chevron" />
                      </button>
                      {isOpen && <div className="faq-answer"><p>{item.a}</p></div>}
                    </div>
                  );
                })}
              </div>
            </section>
          ))
        )}
      </div>

      {/* Still need help CTA */}
      <section className="faq-help-card">
        <div>
          <h3>Still have questions?</h3>
          <p>Our customer support team is available Monday to Saturday to assist you.</p>
        </div>
        <Link to="/contact" className="button button--primary">
          Contact Us <ArrowRight size={17} />
        </Link>
      </section>
    </main>
  );
}
